import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts:
//
//   POST /admin/list_category   { limit, page, search }
//                              → { success, data: { docs: [{_id, name, ...}], totalDocs, ... }, message }
//
//   POST /admin/category_search { q }
//                              → { success, data: [{_id, name}], message }
// ───────────────────────────────────────────────────────────────────────

export type CategoryOption = {
  _id: string
  name: string
  s3response?: unknown
  cover_image?: string
  cover_image_name?: string
  order?: number
  /** V-Q10: admin soft-hide toggle. True = hidden from users (still
   *  visible in admin lists). */
  is_hidden?: boolean
  created_at?: string
  updated_at?: string
}

const CATEGORY_FETCH_LIMIT = 200

/**
 * Fetches all categories for dropdown population. Cached aggressively
 * since categories change rarely.
 */
export function useCategories() {
  return useQuery({
    queryKey: ["admin", "categories", "all"] as const,
    queryFn: async (): Promise<CategoryOption[]> => {
      const res = await api.post<{
        success: boolean
        data: { docs?: CategoryOption[] }
        message: string
      }>("/admin/list_category", {
        limit: CATEGORY_FETCH_LIMIT,
        page: 1,
        search: "",
      })
      const docs = res.data?.data?.docs
      return Array.isArray(docs) ? docs : []
    },
    staleTime: 5 * 60_000,
  })
}

/**
 * Paginated list (used by future Categories list page).
 */
type CategoryListParams = {
  page: number
  limit: number
  search?: string
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type CategoryListResponse = {
  docs: CategoryOption[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export function useCategoriesList(params: CategoryListParams) {
  return useQuery({
    queryKey: ["admin", "categories", "list", params] as const,
    queryFn: async (): Promise<CategoryListResponse> => {
      const res = await api.post<{
        success: boolean
        data: CategoryListResponse
        message: string
      }>("/admin/list_category", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/delete_category", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "categories"] })
    },
  })
}

// ───────────────────────────────────────────────────────────────────────
// Get + Create + Update — note the asymmetric route names that the legacy
// admin uses (preserved exactly):
//
//   POST /admin/category_get  { id }
//   POST /admin/add_category  { name, cover_image, cover_image_name, order }
//   POST /admin/update_category { id, name, cover_image, cover_image_name, order }
//
// `cover_image` accepts EITHER a URL string (when editing without picking a
// new file — backend keeps existing) OR the s3response object (new upload).
// Legacy sends whichever; we choose to OMIT the field on edit-without-new
// so the backend keeps the existing cover, matching the album pattern.
// ───────────────────────────────────────────────────────────────────────

export function useGetCategory(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "categories", "get", id] as const,
    queryFn: async (): Promise<CategoryOption | null> => {
      if (!id) return null
      const res = await api.post<{
        success: boolean
        data: CategoryOption
        message: string
      }>("/admin/category_get", { id })
      return res.data?.data ?? null
    },
    enabled: !!id,
  })
}

type CategoryWritePayload = {
  /** Present for update; omitted for create. */
  id?: string
  name: string
  /** Either an s3response object (new upload) or omitted (keep existing).
   *  Legacy also accepts a URL string here; we never send that shape. */
  cover_image?: unknown
  cover_image_name?: string
  order: number
  /** V-Q10: admin soft-hide toggle. Only send when explicitly changed;
   *  backend leaves the persisted value alone when omitted. */
  is_hidden?: boolean
}

export function useCreateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CategoryWritePayload) => {
      const res = await api.post<{
        success: boolean
        data?: CategoryOption
        message: string
      }>("/admin/add_category", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "categories"] })
    },
  })
}

export function useUpdateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CategoryWritePayload) => {
      const res = await api.post<{
        success: boolean
        data?: CategoryOption
        message: string
      }>("/admin/update_category", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "categories"] })
    },
  })
}
